import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useShallow } from 'zustand/shallow'

import { useTransitionStore } from '@/contexts/transition/use-transition-store'
import { useLoaderStore } from '@/store/loader'

import { createLinesMotion } from './shared'

export const useHeroMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const [landing, reveal] = useTransitionStore(useShallow((state) => [state.landing, state.reveal]))
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const bgTimeline = useRef<GSAPTimeline | null>(null)
  const uiTimeline = useRef<GSAPTimeline | null>(null)

  useGSAP(
    ({ selector }) => {
      if (!fontsReady) return

      const media = selector?.('[data-motion-media]')[0]
      const label = selector?.('[data-motion-label]')[0]
      const pretitle = selector?.('[data-motion-pretitle]')[0]
      const title = selector?.('[data-motion-title]')[0]
      const button = selector?.('[data-motion-button]')[0]
      const scroll = selector?.('[data-motion-scroll]')[0]
      const carousel = selector?.('[data-motion-carousel]')[0]

      const labelMotion = label ? createLinesMotion(label) : null
      const pretitleMotion = pretitle ? createLinesMotion(pretitle) : null
      const titleMotion = title ? createLinesMotion(title) : null

      bgTimeline.current = gsap.timeline({ paused: true })
      uiTimeline.current = gsap.timeline({ paused: true })

      media &&
        bgTimeline.current.fromTo(
          media,
          { scale: 1.1 },
          { scale: 1, duration: 1.8, ease: 'power1.out' },
          0
        )

      labelMotion && uiTimeline.current.add(labelMotion.timeline, 0)
      pretitleMotion && uiTimeline.current.add(pretitleMotion.timeline, 0)
      titleMotion && uiTimeline.current.add(titleMotion.timeline, 0)
      button &&
        uiTimeline.current.fromTo(
          button,
          { autoAlpha: 0, y: 20 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' },
          '<+0.2'
        )
      scroll &&
        uiTimeline.current.fromTo(
          scroll,
          { autoAlpha: 0, y: 10 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' },
          '<+0.2'
        )
      carousel &&
        uiTimeline.current.fromTo(
          carousel,
          { autoAlpha: 0, xPercent: 100 },
          { autoAlpha: 1, xPercent: 0, duration: 0.8, ease: 'power2.out' },
          '<+0.2'
        )
    },
    { scope: motionRef, dependencies: [fontsReady] }
  )

  useGSAP(
    () => {
      if (landing || reveal) {
        bgTimeline.current?.play().delay(0)
        uiTimeline.current?.play().delay(landing ? 0.5 : 0.2)
      }
    },
    { scope: motionRef, dependencies: [landing, reveal] }
  )

  return { motionRef }
}
